import { alpha, type Theme, type Components } from '@mui/material/styles';
import { inputLabelClasses } from '@mui/material/InputLabel';
import { outlinedInputClasses } from '@mui/material/OutlinedInput';
import { svgIconClasses } from '@mui/material/SvgIcon';
import { toggleButtonGroupClasses } from '@mui/material/ToggleButtonGroup';
import { toggleButtonClasses } from '@mui/material/ToggleButton';

import { IconChevronDown } from '@/components/icon/tabler/IconChevronDown';
import { IconCheck as CheckRoundedIcon } from '@/components/icon/tabler/IconCheck';
import { IconMinus as RemoveRoundedIcon } from '@/components/icon/tabler/IconMinus';
import { EmptyCheckboxIcon as CheckBoxOutlineBlankRoundedIcon } from '@/components/icon/controlIcons';
import { gray, controlTokens, surfaceTokens } from '@/theme/themePrimitives';

export const inputsCustomizations: Components<Theme> = {
  MuiNativeSelect: { defaultProps: { IconComponent: IconChevronDown } },
  MuiButtonBase: {
    defaultProps: {
      disableTouchRipple: true,
      disableRipple: true,
    },
    styleOverrides: {
      root: () => ({
        boxSizing: 'border-box',
        transition: 'all 100ms ease-in',
        '&:focus-visible': {
          outline: `3px solid ${alpha(controlTokens.cobalt, 0.5)}`,
          outlineOffset: '2px',
        },
        '&.MuiButton-loading': {
          fontSize: 0,
          '& .MuiButton-icon': {
            opacity: 0,
          },
        },
      }),
    },
  },
  MuiButton: {
    defaultProps: { size: 'medium' },
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: '999px',
        boxShadow: 'none',
        textTransform: 'none',
        whiteSpace: 'nowrap',
        fontWeight: 600,
        transition: 'background-color 120ms ease, border-color 120ms ease, box-shadow 120ms ease',
        '&:focus-visible': { boxShadow: controlTokens.focusRing },
        '&.Mui-disabled': {
          color: controlTokens.disabled,
          backgroundColor: surfaceTokens.section,
          borderColor: surfaceTokens.borderSubtle,
          boxShadow: 'none',
        },
        variants: [
          { props: { size: 'xsmall' }, style: { height: 28, padding: '0 10px', fontSize: 11 } },
          { props: { size: 'small' }, style: { height: 32, padding: '0 12px' } },
          { props: { size: 'medium' }, style: { height: 40, padding: '0 16px' } },
          { props: { size: 'large' }, style: { height: 48, padding: '0 20px' } },
          {
            props: { variant: 'contained', color: 'primary' },
            style: {
              color: '#fff',
              backgroundColor: controlTokens.primary,
              '&:hover': {
                backgroundColor: controlTokens.primaryHover,
                boxShadow: surfaceTokens.shadowPaper,
              },
              '&:active': { backgroundColor: controlTokens.primaryPressed, boxShadow: 'none' },
            },
          },
          {
            props: { variant: 'contained', color: 'secondary' },
            style: {
              color: controlTokens.primary,
              backgroundColor: controlTokens.coral,
              '&:hover': { backgroundColor: controlTokens.coralHover },
              '&:active': { backgroundColor: controlTokens.coralPressed },
            },
          },
          {
            props: { variant: 'contained', color: 'error' },
            style: {
              color: '#fff',
              backgroundColor: controlTokens.error,
              '&:hover': { backgroundColor: controlTokens.errorHover },
              '&:active': { backgroundColor: controlTokens.errorPressed },
            },
          },
          {
            props: { variant: 'contained', color: 'success' },
            style: {
              color: '#fff',
              backgroundColor: controlTokens.successHover,
              '&:hover': { backgroundColor: controlTokens.successPressed },
            },
          },
          {
            props: { variant: 'contained', color: 'info' },
            style: {
              color: controlTokens.primary,
              backgroundColor: controlTokens.infoFill,
              '&:hover': { backgroundColor: controlTokens.infoFillHover },
            },
          },
          {
            props: { variant: 'contained', color: 'warning' },
            style: {
              color: controlTokens.primary,
              backgroundColor: controlTokens.warningFill,
              '&:hover': { backgroundColor: controlTokens.warningFillHover },
            },
          },
          {
            props: { variant: 'contained', color: 'inherit' },
            style: {
              color: 'inherit',
              backgroundColor: surfaceTokens.section,
              '&:hover': { backgroundColor: surfaceTokens.borderSubtle },
            },
          },
          {
            props: { variant: 'outlined' },
            style: {
              color: controlTokens.primary,
              backgroundColor: surfaceTokens.paper,
              border: `1px solid ${surfaceTokens.borderField}`,
              '&:hover': {
                backgroundColor: surfaceTokens.subtle,
                borderColor: controlTokens.primary,
              },
              '&:active': { backgroundColor: surfaceTokens.section },
            },
          },
          {
            props: { variant: 'bordered' },
            style: {
              color: controlTokens.primary,
              backgroundColor: surfaceTokens.paper,
              border: `1px solid ${surfaceTokens.borderField}`,
              '&:hover': {
                backgroundColor: surfaceTokens.subtle,
                borderColor: controlTokens.primary,
              },
              '&:active': { backgroundColor: surfaceTokens.section },
            },
          },
          {
            props: { variant: 'outlined', color: 'error' },
            style: {
              color: controlTokens.error,
              backgroundColor: controlTokens.errorSoft,
              borderColor: controlTokens.errorBorder,
              '&:hover': {
                backgroundColor: controlTokens.errorSoftHover,
                borderColor: controlTokens.error,
              },
              '&:active': { backgroundColor: controlTokens.errorBorder },
            },
          },
          {
            props: { variant: 'text' },
            style: {
              color: controlTokens.primary,
              '&:hover': { backgroundColor: surfaceTokens.interactiveHover },
              '&:active': { backgroundColor: surfaceTokens.interactivePressed },
            },
          },
          {
            props: { variant: 'text', color: 'error' },
            style: {
              color: controlTokens.error,
              '&:hover': { backgroundColor: controlTokens.errorSoft },
              '&:active': { backgroundColor: controlTokens.errorSoftHover },
            },
          },
          {
            props: { variant: 'text', color: 'inherit' },
            style: { color: 'inherit' },
          },
        ],
        ...theme.applyStyles('dark', {
          '&.Mui-disabled': { backgroundColor: gray[800], color: gray[400] },
        }),
      }),
    },
  },
  MuiIconButton: {
    defaultProps: { size: 'small' },
    styleOverrides: {
      root: () => ({
        borderRadius: '999px',
        color: controlTokens.primary,
        border: `1px solid ${surfaceTokens.borderField}`,
        backgroundColor: surfaceTokens.paper,
        '&:hover': { backgroundColor: surfaceTokens.subtle, borderColor: controlTokens.primary },
        '&:active': { backgroundColor: surfaceTokens.section },
        '&:focus-visible': { boxShadow: controlTokens.focusRing },
        '&.Mui-disabled': {
          color: controlTokens.disabled,
          borderColor: surfaceTokens.borderSubtle,
          backgroundColor: surfaceTokens.section,
        },
        variants: [
          {
            props: { size: 'xsmall' },
            style: {
              width: 28,
              height: 28,
              padding: 4,
              [`& .${svgIconClasses.root}`]: { fontSize: 14 },
            },
          },
          {
            props: { size: 'small' },
            style: {
              width: 32,
              height: 32,
              padding: 6,
              [`& .${svgIconClasses.root}`]: { fontSize: 16 },
            },
          },
          { props: { size: 'medium' }, style: { width: 40, height: 40, padding: 8 } },
          ...(
            [
              ['rounded-default', controlTokens.primary, surfaceTokens.section],
              ['rounded-error', controlTokens.error, controlTokens.errorSoft],
              ['rounded-info', controlTokens.info, controlTokens.infoSoft],
              ['rounded-warning', controlTokens.warning, controlTokens.warningSoft],
              ['rounded-success', controlTokens.success, controlTokens.successSoft],
            ] as const
          ).map(([color, foreground, background]) => ({
            props: { color },
            style: {
              borderColor: 'transparent',
              color: foreground,
              backgroundColor: background,
              '&:hover': {
                backgroundColor: background,
                boxShadow: `inset 0 0 0 1px ${foreground}`,
              },
              '&:active': { boxShadow: `inset 0 0 0 2px ${foreground}` },
            },
          })),
        ],
      }),
    },
  },
  MuiButtonGroup: {
    styleOverrides: {
      root: {
        borderRadius: '999px',
        boxShadow: 'none',
        '& .MuiButtonGroup-grouped': {
          borderColor: surfaceTokens.borderField,
          '&:focus-visible': { zIndex: 1 },
        },
      },
    },
  },
  MuiToggleButtonGroup: {
    styleOverrides: {
      root: () => ({
        borderRadius: '999px',
        height: 40,
        padding: 2,
        border: `1px solid ${surfaceTokens.borderField}`,
        backgroundColor: surfaceTokens.paper,
        boxShadow: 'none',
        [`& .${toggleButtonClasses.root}`]: { minHeight: 34, height: 34, padding: '4px 12px' },
        [`& .${toggleButtonGroupClasses.selected}`]: {
          color: '#fff',
          backgroundColor: controlTokens.primary,
        },
      }),
    },
  },
  MuiToggleButton: {
    styleOverrides: {
      root: () => ({
        minHeight: 32,
        padding: '4px 12px',
        textTransform: 'none',
        whiteSpace: 'nowrap',
        borderRadius: '999px !important',
        border: 'none',
        color: controlTokens.primary,
        fontWeight: 600,
        '&:hover': { backgroundColor: surfaceTokens.interactiveHover },
        [`&.${toggleButtonClasses.selected}`]: {
          color: '#fff',
          backgroundColor: controlTokens.primary,
          '&:hover': { backgroundColor: controlTokens.primaryHover },
        },
        '&:focus-visible': { boxShadow: controlTokens.focusRing },
        variants: [
          {
            props: { size: 'medium' },
            style: { minHeight: 40, padding: '8px 16px' },
          },
        ],
      }),
    },
  },
  MuiCheckbox: {
    defaultProps: {
      disableRipple: true,
      icon: <CheckBoxOutlineBlankRoundedIcon sx={{ color: 'hsla(210, 0%, 0%, 0.0)' }} />,
      checkedIcon: <CheckRoundedIcon sx={{ height: 14, width: 14 }} />,
      indeterminateIcon: <RemoveRoundedIcon sx={{ height: 14, width: 14 }} />,
    },
    styleOverrides: {
      root: ({ ownerState }) => ({
        height: 18,
        width: 18,
        borderRadius: 4,
        padding: 0,
        border: '1.5px solid',
        borderColor: surfaceTokens.borderField,
        backgroundColor: surfaceTokens.paper,
        transition: 'border-color 120ms ease-in, background-color 120ms ease-in',
        '&:hover': {
          borderColor: controlTokens.cobalt,
          backgroundColor: surfaceTokens.interactiveHover,
        },
        '&.Mui-focusVisible': {
          outline: `3px solid ${alpha(controlTokens.cobalt, 0.35)}`,
          outlineOffset: '2px',
          borderColor: controlTokens.cobalt,
        },
        '&.Mui-checked': {
          color: 'white',
          backgroundColor: controlTokens.primary,
          borderColor: controlTokens.primary,
          '&:hover': {
            backgroundColor: controlTokens.primaryHover,
            borderColor: controlTokens.primaryHover,
          },
        },
        '&.MuiCheckbox-indeterminate': {
          color: 'white',
          backgroundColor: controlTokens.primary,
          borderColor: controlTokens.primary,
        },
        ...(ownerState.color === 'error' && {
          borderColor: controlTokens.error,
          '&:hover': {
            borderColor: controlTokens.errorHover,
            backgroundColor: controlTokens.errorSoft,
          },
          '&.Mui-checked, &.MuiCheckbox-indeterminate': {
            backgroundColor: controlTokens.error,
            borderColor: controlTokens.error,
            '&:hover': {
              backgroundColor: controlTokens.errorHover,
              borderColor: controlTokens.errorHover,
            },
          },
        }),
        '&.Mui-disabled': {
          opacity: 0.5,
        },
      }),
    },
  },
  MuiInputBase: {
    defaultProps: {
      size: 'medium',
    },
    styleOverrides: {
      root: ({ ownerState }) => ({
        ...(ownerState?.multiline && {
          height: 'auto !important',
        }),
        border: 'none',
        fontSize: 14,
      }),
      input: ({ ownerState }) => ({
        '@media (max-width: 599.95px)': { fontSize: 16 },
        ...(ownerState?.multiline &&
          ownerState?.size === 'small' && {
            minHeight: 'auto !important',
            '.MuiTypography-root': {
              fontSize: '0.825rem',
              height: '14px',
              lineHeight: 1,
            },
          }),
        '&::placeholder': {
          opacity: 1,
          color: gray[500],
        },
        padding: 0,
      }),
    },
  },
  MuiFormControl: {
    variants: [
      {
        props: { size: 'small' },
        style: {
          '& .MuiOutlinedInput-root': { height: 32, borderRadius: 9 },
        },
      },
    ],
  },
  MuiSwitch: {
    styleOverrides: {
      root: {
        width: 46,
        height: 28,
        padding: 0,
        '& .MuiSwitch-switchBase': {
          padding: 2,
          '&.Mui-checked': {
            transform: 'translateX(18px)',
            '& + .MuiSwitch-track': {
              backgroundColor: controlTokens.primary,
              opacity: 1,
            },
          },
          '&.Mui-disabled + .MuiSwitch-track': {
            opacity: 0.4,
          },
          '& + .MuiSwitch-track': {
            backgroundColor: surfaceTokens.borderField,
            opacity: 1,
          },
        },
        '& .MuiSwitch-track': {
          borderRadius: 18,
          width: '100%',
          height: '100%',
          boxSizing: 'border-box',
        },
        '& .MuiSwitch-switchBase.Mui-focusVisible + .MuiSwitch-track': {
          outline: `3px solid ${alpha(controlTokens.cobalt, 0.35)}`,
          outlineOffset: 2,
        },
        '& .MuiSwitch-thumb': {
          width: 24,
          height: 24,
          backgroundColor: 'white',
          boxShadow: '0 2px 4px rgba(0,0,0,0.25)',
          position: 'relative',
        },
        '& .MuiSwitch-switchBase.Mui-checked .MuiSwitch-thumb::after': {
          content: '""',
          position: 'absolute',
          width: 6,
          height: 10,
          left: 9,
          top: 5,
          border: `solid ${controlTokens.primary}`,
          borderWidth: '0 2px 2px 0',
          transform: 'rotate(45deg)',
        },
        variants: [
          {
            props: { size: 'small' },
            style: {
              width: 36,
              height: 22,
              '& .MuiSwitch-switchBase': {
                padding: 2,
                '&.Mui-checked': {
                  transform: 'translateX(14px)',
                  '& + .MuiSwitch-track': {
                    backgroundColor: controlTokens.primary,
                    opacity: 1,
                  },
                },
                '&.Mui-disabled + .MuiSwitch-track': {
                  opacity: 0.4,
                },
                '& + .MuiSwitch-track': {
                  backgroundColor: surfaceTokens.borderField,
                  opacity: 1,
                },
              },
              '& .MuiSwitch-track': {
                borderRadius: 11,
              },
              '& .MuiSwitch-thumb': {
                width: 18,
                height: 18,
                boxShadow: '0 1px 3px rgba(0,0,0,0.25)',
              },
              '& .MuiSwitch-switchBase.Mui-checked .MuiSwitch-thumb::after': {
                width: 4,
                height: 7,
                left: 7,
                top: 4,
                borderWidth: '0 1.5px 1.5px 0',
              },
            },
          },
        ],
      },
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      input: {
        padding: 0,
        '&:-webkit-autofill': {
          WebkitBoxShadow: '0 0 0 100px transparent inset',
          WebkitTextFillColor: 'inherit',
          transition: 'background-color 5000s ease-in-out 0s',
        },
        '&:autofill': {
          boxShadow: '0 0 0 100px transparent inset',
          textFillColor: 'inherit',
        },
        '&:-internal-autofill-selected': {
          backgroundColor: 'transparent !important',
          WebkitBoxShadow: '0 0 0 100px transparent inset',
        },
      },
      root: ({ theme }) => ({
        padding: '8px 12px',
        color: (theme.vars || theme).palette.text.primary,
        fontSize: 14,
        borderRadius: 10,
        backgroundColor: surfaceTokens.paper,
        // The visible border is the notched outline (fieldset), not the root, so a floating
        // `label` opens a gap in it instead of the line running through the text.
        [`&:hover .${outlinedInputClasses.notchedOutline}`]: {
          borderColor: controlTokens.cobalt,
        },
        [`&.${outlinedInputClasses.focused}`]: {
          outline: `4px solid ${alpha(controlTokens.cobalt, 0.13)}`,
        },
        [`&.${outlinedInputClasses.focused} .${outlinedInputClasses.notchedOutline}`]: {
          borderColor: controlTokens.cobalt,
          borderWidth: 1,
        },

        [`&.${outlinedInputClasses.error} .${outlinedInputClasses.notchedOutline}`]: {
          borderColor: controlTokens.error,
        },

        [`&.${outlinedInputClasses.error}.${outlinedInputClasses.focused}`]: {
          outline: `4px solid ${alpha(controlTokens.error, 0.13)}`,
        },
        [`&.${outlinedInputClasses.error}.${outlinedInputClasses.focused} .${outlinedInputClasses.notchedOutline}`]:
          {
            borderColor: controlTokens.error,
          },
        // A floating label sits on the border, so the halo would run behind it and fill the notch.
        // A labelled field marks focus with a thicker border instead.
        [`.${inputLabelClasses.root} + &.${outlinedInputClasses.focused}`]: {
          outline: 'none',
        },
        [`.${inputLabelClasses.root} + &.${outlinedInputClasses.focused} .${outlinedInputClasses.notchedOutline}`]:
          {
            borderWidth: 2,
          },

        [`&.${outlinedInputClasses.disabled}`]: {
          backgroundColor: surfaceTokens.page,
          color: gray[400],
        },
        [`&.${outlinedInputClasses.disabled} .${outlinedInputClasses.notchedOutline}`]: {
          borderColor: surfaceTokens.borderField,
        },
        // An OutlinedInput given `label` without a rendered InputLabel keeps a closed border.
        [`&:not(.${inputLabelClasses.root} + &) > .${outlinedInputClasses.notchedOutline} > legend`]:
          {
            maxWidth: 0.01,
          },
        variants: [
          {
            props: {
              size: 'small',
            },
            style: {
              height: '2rem',
              fontSize: 13,
              paddingLeft: '0.75rem',
            },
          },
          {
            props: {
              size: 'medium',
            },
            style: {
              height: '2.5rem',
            },
          },
        ],
      }),
      notchedOutline: ({ theme }) => ({
        borderColor: surfaceTokens.borderField,
        borderWidth: 1,
        transition: 'border-color 120ms ease-in',
        // Same type as the floating label (16px scaled to 0.75 = 12px), so the notch fits the text.
        '& > legend': {
          fontFamily: theme.typography.subtitle2.fontFamily,
          fontSize: 12,
          fontWeight: 500,
        },
      }),
    },
  },
  MuiInputAdornment: {
    styleOverrides: {
      root: ({ theme }) => ({
        color: (theme.vars || theme).palette.grey[500],
        ...theme.applyStyles('dark', {
          color: (theme.vars || theme).palette.grey[400],
        }),
      }),
    },
  },
  MuiFormLabel: {
    styleOverrides: {
      root: ({ theme }) => ({
        fontFamily: theme.typography.subtitle2.fontFamily,
        fontSize: 13,
        fontWeight: 500,
        lineHeight: 1.4,
        color: controlTokens.primary,
        marginBottom: theme.spacing(1),
        '&.Mui-focused': {
          color: controlTokens.primary,
        },
        '&.Mui-error': {
          color: theme.palette.error.main,
        },
        '&.Mui-error.Mui-focused': {
          color: theme.palette.error.main,
        },
      }),
    },
  },
  MuiInputLabel: {
    styleOverrides: {
      root: ({ theme }) => ({
        fontFamily: theme.typography.subtitle2.fontFamily,
        fontSize: 13,
        fontWeight: 500,
        color: controlTokens.primary,
        '&.MuiInputLabel-outlined:not(.MuiInputLabel-shrink)': {
          transform: 'translate(14px, 11px) scale(1)',
        },
        '&.MuiInputLabel-outlined.MuiInputLabel-sizeSmall:not(.MuiInputLabel-shrink)': {
          transform: 'translate(14px, 7px) scale(1)',
        },
        '&.MuiInputLabel-shrink': {
          fontSize: 16,
        },
        '&.Mui-focused': {
          color: controlTokens.primary,
        },
        '&.Mui-error': {
          color: theme.palette.error.main,
        },
        '&.Mui-error.Mui-focused': {
          color: theme.palette.error.main,
        },
      }),
    },
  },
  MuiFormControlLabel: {
    styleOverrides: {
      root: ({ theme }) => ({
        margin: 0,
        '*': {
          fontSize: '0.825rem',
        },
        '& .MuiCheckbox-root': {
          marginRight: theme.spacing(1),
        },
        '& .MuiRadio-root': {
          marginRight: theme.spacing(1),
        },
        '& .MuiSwitch-root': {
          marginRight: theme.spacing(1),
        },
      }),
    },
  },
  MuiSlider: {
    styleOverrides: {
      root: {
        color: controlTokens.primary,
        height: 6,
        '&.Mui-disabled': {
          opacity: 0.5,
        },
      },
      track: {
        backgroundColor: controlTokens.primary,
        border: 'none',
      },
      rail: {
        backgroundColor: surfaceTokens.borderSubtle,
        opacity: 1,
      },
      thumb: {
        backgroundColor: surfaceTokens.paper,
        border: `2px solid ${controlTokens.primary}`,
        width: 16,
        height: 16,
        '&:hover': {
          boxShadow: `0 0 0 4px ${alpha(controlTokens.cobalt, 0.13)}`,
        },
        '&.Mui-active, &:focus': {
          boxShadow: `0 0 0 8px ${alpha(controlTokens.cobalt, 0.25)}`,
        },
      },
      mark: {
        backgroundColor: controlTokens.primary,
        height: 8,
        width: 2,
      },
      valueLabel: {
        backgroundColor: controlTokens.primary,
        color: surfaceTokens.paper,
        fontSize: 12,
        borderRadius: 2,
      },
    },
  },
  MuiRadio: {
    defaultProps: {
      disableRipple: true,
      icon: (
        <span
          style={{
            display: 'block',
            width: 18,
            height: 18,
            borderRadius: '50%',
            border: `1.5px solid ${surfaceTokens.borderField}`,
            backgroundColor: surfaceTokens.paper,
            boxSizing: 'border-box',
            transition: 'border-color 120ms ease-in',
          }}
        />
      ),
      checkedIcon: (
        <span
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 18,
            height: 18,
            borderRadius: '50%',
            border: `1.5px solid ${controlTokens.primary}`,
            backgroundColor: 'white',
            boxSizing: 'border-box',
          }}
        >
          <span
            style={{
              width: 12,
              height: 12,
              borderRadius: '50%',
              backgroundColor: controlTokens.primary,
            }}
          />
        </span>
      ),
    },
    styleOverrides: {
      root: () => ({
        padding: 1,
        '&.Mui-focusVisible': {
          outline: `3px solid ${alpha(controlTokens.cobalt, 0.35)}`,
          outlineOffset: 2,
        },
        '&.Mui-disabled': {
          opacity: 0.5,
        },
        '&:hover span': {
          borderColor: `${controlTokens.cobalt} !important`,
          '& span': {
            backgroundColor: `${controlTokens.cobalt} !important`,
          },
        },
        '&.Mui-checked:hover span': {
          borderColor: `${controlTokens.primary} !important`,
          '& span': {
            backgroundColor: `${controlTokens.primary} !important`,
          },
        },
      }),
    },
  },
  MuiFormHelperText: {
    styleOverrides: {
      root: ({ theme }) => ({
        marginLeft: 0,
        fontFamily: theme.typography.caption.fontFamily,
        fontSize: 12,
        lineHeight: 1.4,
        color: gray[600],
        '&.Mui-error': { color: controlTokens.error },
      }),
    },
  },
};
